// Follow a creator: an accounts mark `{kind: "follow", target: "cr_…"}` (the same mark accounts counts for
// `followers` on GET /v1/catalogue/creators/:id and notifies through `followersOf`). One tap; signed out, it goes to
// sign-in and comes back.

import { api } from "../../api.ts";
import { h, toast } from "../../dom.ts";
import { navigate } from "../../router.ts";
import { signedIn } from "../../session.ts";

interface Library {
  library: string[];
  hearts: string[];
  follows: { games: string[]; creators: string[] };
}

/** A follow is an accounts mark `{kind: "follow", target: "cr_…"}`; last write wins, so a client timestamp is enough. */
export function followMark(creatorId: string, on: boolean, now = Date.now()) {
  return { kind: "follow" as const, target: creatorId, on, updatedAt: now };
}

let library: Promise<Library> | null = null;

/** The creators the signed-in player follows (one read per page load); [] signed out or when accounts is away. */
export async function followedCreators(): Promise<string[]> {
  if (!signedIn()) return [];
  library ??= api<Library>("accounts", "/me/library").catch((e) => {
    library = null;
    throw e;
  });
  try {
    return (await library).follows?.creators ?? [];
  } catch {
    return [];
  }
}

/**
 * `since` for a sync that only writes: accounts answers every mark changed after `since`, so `0` sent the player's
 * whole mark list back on every tap. A cursor past any real one asks for none (review 2026-10-19).
 */
export const WRITE_ONLY_SINCE = Number.MAX_SAFE_INTEGER;

interface SyncAnswer {
  applied?: number;
  stale?: { kind?: string; target?: string; on?: boolean }[];
}

/**
 * Whether the player follows `creatorId` after the sync answered: what they asked for when it applied; when it lost
 * (a newer write from another device won), the server's value from `stale`; otherwise unchanged (`was`).
 */
export function followAfterSync(answer: SyncAnswer | null | undefined, creatorId: string, wanted: boolean, was: boolean): boolean {
  if ((answer?.applied ?? 0) > 0) return wanted;
  const won = answer?.stale?.find((m) => m.kind === "follow" && m.target === creatorId);
  return typeof won?.on === "boolean" ? won.on : was;
}

/** Sets the follow; answers whether the player now follows (what accounts kept, which may not be what they asked). */
export async function setFollow(creatorId: string, on: boolean, was = !on): Promise<boolean> {
  const r = await api<SyncAnswer>("accounts", "/me/sync", { body: { marks: [followMark(creatorId, on)], since: WRITE_ONLY_SINCE } });
  library = null;
  return followAfterSync(r, creatorId, on, was);
}

/** "1 follower", "1.2k followers"; null for none (no "0 followers" on a page). */
export function followersText(n: number): string | null {
  if (!Number.isFinite(n) || n <= 0) return null;
  const v = n >= 1e6 ? `${(n / 1e6).toFixed(n >= 1e7 ? 0 : 1)}m` : n >= 1000 ? `${(n / 1000).toFixed(n >= 1e4 ? 0 : 1)}k` : String(Math.floor(n));
  return `${v.replace(/\.0(?=[km])/, "")} ${n === 1 ? "follower" : "followers"}`;
}

/** A creator's profile, from accounts' single read (wave 12): what a claimant wrote, plus the live follower count. */
export interface CreatorProfile {
  followers: number;
  bio: string | null;
  avatar: string | null;
  links: { label: string; url: string }[];
}

const NO_PROFILE: CreatorProfile = { followers: 0, bio: null, avatar: null, links: [] };
const httpsUrl = (u: unknown): string | null => {
  if (typeof u !== "string") return null;
  try {
    const url = new URL(u);
    return url.protocol === "https:" ? url.href : null;
  } catch {
    return null;
  }
};

/** Read defensively: accounts validates, but the page renders only text and https links whatever comes back. */
export function parseProfile(raw: unknown): CreatorProfile {
  if (!raw || typeof raw !== "object") return NO_PROFILE;
  const r = raw as { followers?: unknown; creator?: { bio?: unknown; avatar?: unknown; links?: unknown } };
  const c = r.creator ?? {};
  const links = Array.isArray(c.links)
    ? c.links
        .map((l) => (l && typeof l === "object" ? { label: String((l as { label?: unknown }).label ?? "").trim().slice(0, 40), url: httpsUrl((l as { url?: unknown }).url) } : null))
        .filter((l): l is { label: string; url: string } => !!l && !!l.label && !!l.url)
        .slice(0, 5)
    : [];
  return {
    followers: typeof r.followers === "number" && r.followers >= 0 ? r.followers : 0,
    bio: typeof c.bio === "string" && c.bio.trim() ? c.bio.trim().slice(0, 500) : null,
    // Stopgap (review 2026-10-19): a creator's avatar is any https hotlink, and this is a 13+ site. Until the owner
    // picks uploads only or an allowlist of hosts, the site never renders it (accounts still serves the field).
    avatar: null,
    links,
  };
}

const profiles = new Map<string, Promise<CreatorProfile>>();

export function creatorProfile(creatorId: string): Promise<CreatorProfile> {
  let p = profiles.get(creatorId);
  if (!p) {
    p = api<unknown>("accounts", `/catalogue/creators/${encodeURIComponent(creatorId)}`, { auth: false })
      .then(parseProfile)
      .catch(() => NO_PROFILE);
    profiles.set(creatorId, p);
  }
  return p;
}

/** accounts' follower count for a creator (live, on the single creator read); 0 when it can't say. */
export function followerCount(creatorId: string): Promise<number> {
  return creatorProfile(creatorId).then((p) => p.followers);
}

/**
 * The Follow button, the zone's one accent. With `count`, a follower count sits beside it (hidden at 0) and moves
 * with the player's own tap.
 */
export function followButton(creatorId: string, name: string, opts: { count?: boolean; small?: boolean } = {}): HTMLElement {
  let on = false;
  let n = 0;
  const btn = h("button", { type: "button", class: `btn cr-follow${opts.small ? " small" : ""}`, "aria-pressed": "false", "aria-label": `Follow ${name}` }, "Follow");
  const countEl = opts.count ? h("span", { class: "cr-followers", hidden: true }) : null;
  const paint = () => {
    btn.textContent = on ? "Following" : "Follow";
    btn.setAttribute("aria-pressed", on ? "true" : "false");
    btn.setAttribute("aria-label", `${on ? "Following" : "Follow"} ${name}`);
    btn.classList.toggle("on", on);
    if (countEl) {
      const t = followersText(n);
      countEl.textContent = t ?? "";
      countEl.hidden = !t;
    }
  };
  btn.addEventListener("click", async (e) => {
    e.preventDefault();
    e.stopPropagation();
    if (!signedIn()) return navigate(`/signin?next=${encodeURIComponent(location.pathname)}`);
    btn.setAttribute("disabled", "");
    try {
      const wanted = !on;
      const now = await setFollow(creatorId, wanted, on);
      if (now !== on) n = Math.max(0, n + (now ? 1 : -1));
      on = now;
      paint();
      if (now !== wanted) toast(now ? `You already follow ${name} (changed on another device).` : `You'd unfollowed ${name} on another device.`);
      else toast(on ? `Following ${name}. Their new games and devlogs reach you in the Banger app.` : `Unfollowed ${name}`);
    } catch (err) {
      toast(err instanceof Error ? err.message : String(err));
    } finally {
      btn.removeAttribute("disabled");
    }
  });
  void followedCreators().then((ids) => ((on = ids.includes(creatorId)), paint()));
  if (countEl) void followerCount(creatorId).then((c) => ((n = Math.max(n, c)), paint()));
  return countEl ? h("div", { class: "cr-follow-wrap" }, btn, countEl) : btn;
}
